import { useCallback, useEffect, useRef, useState } from "react";

// 'pending'     the browser has not answered yet (it may be asking the user for permission)
// 'ready'       `position` is the position of the user
// 'denied'      the user refused access to the location
// 'unavailable' no geolocation in this browser, or it could not be determined (e.g. timeout)
const OPTIONS = { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 }

// The position of the person using the web application, as { lat, lng } for Google Maps.
// The browser only allows this on HTTPS pages and on localhost.
export const useUserPosition = () => {
  const supported = typeof navigator !== "undefined" && !!navigator.geolocation
  const [state, setState] = useState({ position: null, status: supported ? "pending" : "unavailable" })
  const mounted = useRef(true)

  const locate = useCallback(() => {
    if (!supported) return
    setState(current => ({ ...current, status: "pending" }))
    navigator.geolocation.getCurrentPosition(
      (result) => {
        if (!mounted.current) return
        setState({ position: { lat: result.coords.latitude, lng: result.coords.longitude }, status: "ready" })
      },
      (error) => {
        if (!mounted.current) return
        // PERMISSION_DENIED is code 1; a position found earlier stays valid
        setState(current => ({ position: current.position, status: error.code === 1 ? "denied" : "unavailable" }))
      },
      OPTIONS
    )
  }, [supported])

  useEffect(() => {
    mounted.current = true
    locate()
    return () => { mounted.current = false }
  }, [locate])

  return { ...state, locate }
}
